import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Film, Mic } from "lucide-react";
import PublicShell from "../../components/PublicShell";
import api from "../../lib/api";
import usePageMeta from "../../lib/usePageMeta";

const SITE = "https://thedialstream.com";
const CONFIG = {
  indie: {
    endpoint: "/public/indie", path: "/indie", Icon: Film, kicker: "Indie Spotlight",
    h1: "Independent films on The Dial", aspect: "aspect-[2/3]",
    title: "Indie Spotlight — Independent Films to Watch | The Dial",
    desc: "Independent films from working filmmakers, each linking straight to where you can watch it. Discover them on The Dial.",
    empty: "No films are in Indie Spotlight yet.", cta: ["/submit", "Submit your film"],
    sub: (i) => [i.director, i.year].filter(Boolean).join(" · "),
  },
  podcasts: {
    endpoint: "/podcasts", path: "/podcasts", Icon: Mic, kicker: "Podcast Spotlight",
    h1: "TV & film podcasts on The Dial", aspect: "aspect-square",
    title: "Podcast Spotlight — TV & Film Podcasts | The Dial",
    desc: "Podcasts about TV and film, each linking straight to where you can listen. Discover them on The Dial.",
    empty: "No podcasts are in Podcast Spotlight yet.", cta: ["/submit?tab=podcast", "Submit your podcast"],
    sub: (i) => i.host,
  },
};

function Tile({ i, c, kind }) {
  return (
    <Link to={`${c.path}/${i.slug}`} className="group block" data-testid={`public-${kind}-item-${i.slug}`}>
      {i.poster
        ? <img src={i.poster} alt={`${i.title} ${kind === "indie" ? "poster" : "cover art"}`} loading="lazy" className={`${c.aspect} w-full rounded-[var(--card-radius)] border border-[var(--border-subtle)] object-cover transition-transform group-hover:-translate-y-1`} />
        : <div className={`${c.aspect} flex w-full items-center justify-center rounded-[var(--card-radius)] border border-[var(--border-subtle)] bg-[var(--bg-card)] text-[var(--text-muted)]`}><c.Icon size={32} /></div>}
      <p className="mt-2 truncate text-sm font-semibold text-[var(--text-primary)]">{i.title}</p>
      {c.sub(i) && <p className="truncate text-xs text-[var(--text-muted)]">{c.sub(i)}</p>}
    </Link>
  );
}

export default function SpotlightIndex({ kind }) {
  const c = CONFIG[kind];
  const [items, setItems] = useState(null);
  useEffect(() => {
    setItems(null);
    api.get(c.endpoint).then(({ data }) => setItems(data.items || [])).catch(() => setItems([]));
  }, [c.endpoint]);
  usePageMeta({
    title: c.title, description: c.desc,
    jsonLd: items?.length ? { "@context": "https://schema.org", "@type": "ItemList", name: c.kicker,
      itemListElement: items.map((i, n) => ({ "@type": "ListItem", position: n + 1, url: `${SITE}${c.path}/${i.slug}`, name: i.title })) } : null,
  });
  return (
    <PublicShell testid={`public-${kind}-index`}>
      <p className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.22em] text-[var(--accent-hover)]"><c.Icon size={14} /> {c.kicker}</p>
      <h1 className="mt-2 font-head text-4xl sm:text-5xl text-[var(--text-primary)]">{c.h1}</h1>
      <p className="mt-3 max-w-2xl text-sm text-[var(--text-secondary)]">{c.desc}</p>
      {items === null && <p className="mt-10 text-[var(--text-secondary)]">Loading…</p>}
      {items && items.length === 0 && (
        <div className="mt-10 text-[var(--text-secondary)]" data-testid={`public-${kind}-empty`}>
          {c.empty} <Link to={c.cta[0]} className="font-semibold text-[var(--accent-hover)] underline underline-offset-2">{c.cta[1]}</Link>
        </div>
      )}
      {items && items.length > 0 && (
        <div className="mt-8 grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-5" data-testid={`public-${kind}-grid`}>
          {items.map((i) => <Tile key={i.slug} i={i} c={c} kind={kind} />)}
        </div>
      )}
    </PublicShell>
  );
}
